/**
 * The template's version history (D10) — read when opened, not before.
 *
 * WHAT THE SERVER STORED, NOTHING ELSE. Each row is one stored version: its
 * number, whether it was an own template or the step back to the system
 * template, when, and by whom — a name, else an e-mail, else "eine unbekannte
 * Person" for an actor that no longer resolves; a row without an actor names
 * nobody. The system template itself has no version and never appears as one:
 * only the "back to the system template" steps do, as the markers they are.
 * Rows carry no documents, so there is no "view this version" here.
 *
 * CAPPED BY THE SERVER, AND SAYING SO when `total` exceeds the page.
 */
import React, { useId, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

import { formatDateTime } from "../../../utils/datetime";
import { historySourceLabel } from "./automationTemplateLabels";

const BUTTON =
  "inline-flex items-center gap-1.5 rounded-[8px] border border-border bg-card px-2.5 py-1 text-[12px] font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-md:min-h-9";

function authorOf(version) {
  if (version.created_by_full_name) return version.created_by_full_name;
  if (version.created_by_email) return version.created_by_email;
  if (version.created_by_user_id) return "eine unbekannte Person";
  return null;
}

export default function AutomationTemplateHistory({ versions, latestVersion, onLoad }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && versions.status === "idle") onLoad();
  };

  return (
    <section aria-label="Versionsverlauf" className="flex flex-col gap-2">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={toggle}
        className="inline-flex items-center gap-1.5 self-start rounded-[8px] px-1 py-0.5 text-[13px] font-semibold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-md:min-h-9"
      >
        {open ? (
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        )}
        Versionsverlauf
      </button>

      {open ? (
        <div id={panelId} className="flex flex-col gap-2">
          {versions.status === "loading" ? (
            <p className="m-0 text-[12.5px] text-muted-foreground">
              Versionsverlauf wird geladen …
            </p>
          ) : null}
          {versions.status === "error" ? (
            <div className="flex flex-col items-start gap-2">
              <p role="alert" className="m-0 text-[12.5px] text-destructive">
                {versions.error?.message}
              </p>
              <button type="button" onClick={onLoad} className={BUTTON}>
                Erneut versuchen
              </button>
            </div>
          ) : null}
          {versions.status === "ready" && !versions.items.length ? (
            <p className="m-0 text-[12.5px] text-muted-foreground">
              Noch keine Version gespeichert — es gilt die Systemvorlage.
            </p>
          ) : null}
          {versions.items.length ? (
            <ol
              aria-label="Versionen"
              className="m-0 flex list-none flex-col gap-0 overflow-hidden rounded-[12px] border border-border bg-card p-0"
            >
              {versions.items.map((version) => {
                const author = authorOf(version);
                const current = latestVersion !== null && version.version === latestVersion;
                return (
                  <li
                    key={version.version}
                    className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-border px-3.5 py-2.5 last:border-b-0"
                  >
                    <span className="flex min-w-0 flex-col gap-0.5">
                      <span className="text-[12.5px] font-semibold text-foreground">
                        Version {version.version} · {historySourceLabel(version.source)}
                        {current ? (
                          <span className="ml-2 rounded-full border border-border px-1.5 py-px text-[10.5px] font-medium text-muted-foreground">
                            aktuell
                          </span>
                        ) : null}
                      </span>
                      {author ? (
                        <span className="break-words text-[11.5px] text-muted-foreground">
                          von {author}
                        </span>
                      ) : null}
                    </span>
                    <time
                      dateTime={version.created_at}
                      className="shrink-0 text-[11.5px] tabular-nums text-muted-foreground"
                    >
                      {formatDateTime(version.created_at)}
                    </time>
                  </li>
                );
              })}
            </ol>
          ) : null}
          {versions.total > versions.items.length ? (
            <p className="m-0 text-[11.5px] text-muted-foreground">
              Die neuesten {versions.items.length} von {versions.total} Versionen.
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
